import { useSectionAccess } from "../../../auth/useSectionAccess";
import { FormEvent, useCallback, useEffect, useState, type CSSProperties } from "react";
import { PanelLoading } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import {
  settlementApi,
  type RuleDirection,
  type SettlementRuleSet,
} from "../../../api/settlement";
import { qualityApi, type QualityParameter } from "../../../api/quality";
import type { ApiError } from "../../../api/client";
import { useToast } from "../../../components/Toast";
import { Select } from "../../../components/Select";
import { localizedName } from "../../../lib/localizedName";
import Ic from "../../../components/icons.jsx";

/**
 * Settlement rules.
 *
 * These are the numbers that turn a laboratory reading into money. The mechanism
 * shipped before Bankom's actual figures were known — this page is where they get
 * entered, so nobody has to deploy code to change a penalty rate.
 *
 * Tolerance is worth reading carefully: it is deducted FROM the deviation, not a
 * threshold that releases the whole deviation once crossed.
 */
export default function AdminSettlementRulesPage() {
  const { canWrite, canFull } = useSectionAccess("settlement_rules");
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const [sets, setSets] = useState<SettlementRuleSet[] | null>(null);
  const [parameters, setParameters] = useState<QualityParameter[]>([]);
  const [creatingSet, setCreatingSet] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [editingRuleFor, setEditingRuleFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([settlementApi.listSets(), qualityApi.listParameters(true)]);
      setSets(s);
      setParameters(p);
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = (parameterId: string) => {
    const p = parameters.find((x) => x.id === parameterId);
    return p
      ? localizedName({ name: p.key, nameI18n: p.nameI18n }, i18n.resolvedLanguage ?? "sr")
      : parameterId;
  };

  async function createSet(e: FormEvent) {
    e.preventDefault();
    try {
      await settlementApi.createSet({ name: newSetName.trim(), isDefault: (sets ?? []).length === 0 });
      setNewSetName("");
      setCreatingSet(false);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function makeDefault(id: string) {
    try {
      await settlementApi.updateSet(id, { isDefault: true });
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function removeRule(setId: string, ruleId: string) {
    try {
      await settlementApi.deleteRule(setId, ruleId);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("admin.settlement.title", "Settlement rules")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t(
              "admin.settlement.subtitle",
              "How a measured parameter moves the price. Tolerance is deducted from the deviation, not a threshold.",
            )}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setCreatingSet(true)}>
            <Ic.plus /> {t("admin.settlement.newSet", "New rule set")}
          </button>
        </div>
      </div>

      {creatingSet && canWrite && (
        <form onSubmit={createSet} style={{ ...card, flexDirection: "row", alignItems: "flex-end", gap: 8 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            <span style={fieldLabel}>{t("admin.settlement.setName", "Rule set name")}</span>
            <input className="input" value={newSetName} onChange={(e) => setNewSetName(e.target.value)} required />
          </label>
          <button type="submit" className="btn primary">{t("common.save", "Save")}</button>
          <button type="button" className="btn" onClick={() => setCreatingSet(false)}>
            {t("common.cancel", "Cancel")}
          </button>
        </form>
      )}

      {!sets ? (
        <PanelLoading text={t("common.loading")} />
      ) : sets.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>
          {t("admin.settlement.empty", "No rule sets yet. Deliveries settle at the agreed price until one exists.")}
        </p>
      ) : (
        sets.map((s) => (
          <section key={s.id} style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontSize: 14 }}>{s.name}</h2>
              {s.isDefault ? (
                <span style={badge}>{t("admin.settlement.isDefault", "Default")}</span>
              ) : (
                <button type="button" className="btn sm" disabled={!canWrite} onClick={() => void makeDefault(s.id)}>
                  {t("admin.settlement.makeDefault", "Make default")}
                </button>
              )}
              <span style={{ flex: 1 }} />
              <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setEditingRuleFor(s.id)}>
                <Ic.plus /> {t("admin.settlement.newRule", "Add rule")}
              </button>
            </div>

            {s.rules.length === 0 ? (
              <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
                {t("admin.settlement.noRules", "No rules — this set corrects nothing.")}
              </p>
            ) : (
              <table className="tbl" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>{t("deliveries.settlement.parameter", "Parameter")}</th>
                    <th>{t("admin.settlement.baseValue", "Base")}</th>
                    <th>{t("admin.settlement.tolerance", "Tolerance")}</th>
                    <th>{t("admin.settlement.direction", "Direction")}</th>
                    <th>{t("admin.settlement.bonus", "Bonus %/point")}</th>
                    <th>{t("admin.settlement.penalty", "Penalty %/point")}</th>
                    <th>{t("admin.settlement.limits", "Accepted range")}</th>
                    <th style={{ textAlign: "right" }}>{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.rules.map((r) => (
                    <tr key={r.id}>
                      <td>{nameOf(r.qualityParameterId)}</td>
                      <td>{r.baseValue}</td>
                      <td>{r.tolerance}</td>
                      <td>{t(`admin.settlement.direction.${r.direction}`, r.direction)}</td>
                      <td>{r.bonusPctPerPoint}</td>
                      <td>{r.penaltyPctPerPoint}</td>
                      <td>
                        {r.minAccepted ?? "—"} … {r.maxAccepted ?? "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button type="button" className="btn sm" disabled={!canFull} onClick={() => void removeRule(s.id, r.id)}>
                          {t("action.delete", "Delete")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {editingRuleFor === s.id && canWrite && (
              <RuleForm
                ruleSetId={s.id}
                parameters={parameters}
                onClose={() => setEditingRuleFor(null)}
                onSaved={async () => {
                  setEditingRuleFor(null);
                  await load();
                }}
              />
            )}
          </section>
        ))
      )}
    </div>
  );
}

function RuleForm({
  ruleSetId,
  parameters,
  onClose,
  onSaved,
}: {
  ruleSetId: string;
  parameters: QualityParameter[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [parameterId, setParameterId] = useState(parameters[0]?.id ?? "");
  const [baseValue, setBaseValue] = useState("");
  const [tolerance, setTolerance] = useState("0");
  const [direction, setDirection] = useState<RuleDirection>("HIGHER_IS_BETTER");
  const [bonus, setBonus] = useState("0");
  const [penalty, setPenalty] = useState("0");
  const [minAccepted, setMinAccepted] = useState("");
  const [maxAccepted, setMaxAccepted] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await settlementApi.upsertRule(ruleSetId, {
        qualityParameterId: parameterId,
        baseValue: Number(baseValue),
        tolerance: Number(tolerance),
        direction,
        bonusPctPerPoint: Number(bonus),
        penaltyPctPerPoint: Number(penalty),
        minAccepted: minAccepted.trim() === "" ? null : Number(minAccepted),
        maxAccepted: maxAccepted.trim() === "" ? null : Number(maxAccepted),
      });
      toast.success(t("admin.settlement.ruleSaved", "Rule saved."));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
      <div style={grid}>
        <label style={label}>
          <span style={fieldLabel}>{t("deliveries.settlement.parameter", "Parameter")}</span>
          <Select<string>
            value={parameterId}
            onChange={setParameterId}
            ariaLabel={t("deliveries.settlement.parameter", "Parameter")}
            options={parameters.map((p) => ({
              value: p.id,
              label: localizedName({ name: p.key, nameI18n: p.nameI18n }, i18n.resolvedLanguage ?? "sr"),
            }))}
          />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.baseValue", "Base")}</span>
          <input type="number" step="0.001" className="input" value={baseValue} onChange={(e) => setBaseValue(e.target.value)} required />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.tolerance", "Tolerance")}</span>
          <input type="number" step="0.001" min="0" className="input" value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.direction", "Direction")}</span>
          <Select<RuleDirection>
            value={direction}
            onChange={setDirection}
            ariaLabel={t("admin.settlement.direction", "Direction")}
            options={[
              { value: "HIGHER_IS_BETTER", label: t("admin.settlement.direction.HIGHER_IS_BETTER", "Higher is better") },
              { value: "LOWER_IS_BETTER", label: t("admin.settlement.direction.LOWER_IS_BETTER", "Lower is better") },
            ]}
          />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.bonus", "Bonus %/point")}</span>
          <input type="number" step="0.001" min="0" className="input" value={bonus} onChange={(e) => setBonus(e.target.value)} />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.penalty", "Penalty %/point")}</span>
          <input type="number" step="0.001" min="0" className="input" value={penalty} onChange={(e) => setPenalty(e.target.value)} />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.minAccepted", "Refuse below")}</span>
          <input type="number" step="0.001" className="input" value={minAccepted} onChange={(e) => setMinAccepted(e.target.value)} />
        </label>
        <label style={label}>
          <span style={fieldLabel}>{t("admin.settlement.maxAccepted", "Refuse above")}</span>
          <input type="number" step="0.001" className="input" value={maxAccepted} onChange={(e) => setMaxAccepted(e.target.value)} />
        </label>
      </div>
      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
        {t(
          "admin.settlement.toleranceHint",
          "Tolerance is deducted from the deviation: 1.5 over base with 0.5 tolerance is charged as 1.0.",
        )}
      </span>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="btn primary" disabled={busy || !parameterId}>
          {t("common.save", "Save")}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t("common.cancel", "Cancel")}
        </button>
      </div>
    </form>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 12,
};
const label: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const badge: CSSProperties = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "var(--surface-2)",
  color: "var(--ink-2)",
};
